/**
 * Invitations with RSVP (asked for 2026-10-21): "send a group of cards for a group of people
 * ... the invite name, the event type, the address, date and time, a contact for RSVP ...
 * FoldMyCard creates the cards in bulk, each with the guest's name ... a picture code people
 * scan with their phone's camera to confirm, and FoldMyCard notifies the host".
 *
 * One card design, printed once per guest: the message frame carries "[Name]", which each
 * guest's copy fills in, and a QR code that opens that guest's own RSVP page (rsvp.tsx).
 *
 * 2026-10-24, the owner: "I need the card information to stay local to the device". The event's
 * details and the guests' names are kept on this device and in the card files (local/events.ts);
 * the server (apps/api/src/events.ts) has only each guest's code, the card they are on and their
 * answer. A guest's QR code carries their name and the event's name after a "#" - read by their
 * phone, never sent to the server. Here the two halves meet: `useEvent` puts the server's answers
 * together with this device's names.
 *
 * 2026-10-23: an event can have several cards, "so they can release different cards for
 * different people" - each guest is on one of them, and a card prints only its own guests. A
 * card joins an event you already have from here, or from the events board. Deleting an event
 * puts it in the archive (restorable for 30 days), after an "are you sure?" (confirm.tsx).
 */
import { useCallback, useEffect, useState, type FormEvent } from 'react';
import type { TemplateDocument, ZoneText } from '@foldmycard/scene';
import {
  api, EVENT_KINDS, type EventBrief, type EventCard, type EventFull, type EventKind, type EventSummary, type Guest, type ProjectItem, type ServerEvent,
} from './api.ts';
import type { InviteCode } from './card-view.tsx';
import { inviteHash } from './invite-hash.ts';
import { askConfirm } from './confirm.tsx';
import {
  allLocalEvents, getLocalEvent, keepUntilOf, onLocalEvents, putLocalEvent, updateLocalEvent, type EventDetails, type LocalEvent,
} from './local/events.ts';
import { t, type StringKey } from './strings.ts';

/** The placeholder a guest's name takes the place of. */
export const NAME_MARK = '[Name]';

/** The frame an invitation's words and code go in: the card's message. */
export function messageZoneOf(template: TemplateDocument): number | null {
  const z = template.zones.find((x) => x.kind === 'message') ?? template.zones.find((x) => x.defaultContent === 'text');
  return z?.number ?? null;
}

/** Notes with [Name] filled in for one guest. */
export function personalise(notes: Map<number, ZoneText>, name: string): Map<number, ZoneText> {
  const out = new Map<number, ZoneText>();
  for (const [n, note] of notes) out.set(n, { ...note, content: note.content.replace(/\[name\]/gi, name) });
  return out;
}

/** The date and time of an event as people read them, from what the host typed (no time zone shift). */
export function whenOf(ev: Pick<EventDetails, 'startsOn' | 'startsAt'>): string {
  const [y, m, d] = ev.startsOn.split('-').map(Number);
  const [hh, mm] = (ev.startsAt ?? '00:00').split(':').map(Number);
  const at = new Date(y, m - 1, d, hh, mm);
  const day = at.toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
  return ev.startsAt ? `${day}, ${at.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}` : day;
}

export const kindLabel = (k: string) => t(`eventKind_${k}` as StringKey);

/** The invitation's words, for the message frame: [Name] is filled in per guest. */
export function invitationText(ev: EventDetails): string {
  return [
    `${t('inviteDear')} ${NAME_MARK},`,
    t('inviteYouAre'),
    ev.title,
    whenOf(ev),
    ev.place,
    ev.contact ? `${t('inviteRsvp')}: ${ev.contact}` : '',
  ].filter(Boolean).join('\n');
}

/* ------------------------------------------------- an event, as its host sees it */

/** A guest, with their name from this device - or "Guest 3" on a device that does not have it. */
export type NamedGuest = Guest & { name: string; named: boolean };

/** An event as its host sees it: the server's codes, cards and answers, with this device's details and names. */
export interface EventData {
  event: ServerEvent;
  /** The details and names, when this device has them (else they are on another device). */
  details: LocalEvent | null;
  guests: NamedGuest[];
  summary: EventSummary;
  cards: EventCard[];
}

export function withDevice(full: EventFull, local: LocalEvent | null): EventData {
  return {
    event: full.event, details: local, summary: full.summary, cards: full.cards,
    guests: full.guests.map((g, i) => {
      const name = local?.names[g.id];
      return { ...g, name: name ?? t('inviteGuestN', { n: i + 1 }), named: !!name };
    }),
  };
}

/** The code on the card for one guest: their own link, with their name and the event's after "#". */
export const codeFor = (zone: number, guest: NamedGuest, details: LocalEvent | null): InviteCode => ({
  zone, label: t('inviteScan'),
  url: details && guest.named ? `${guest.link}#${inviteHash(guest.name, details.title)}` : guest.link,
});
/** A preview with no guests yet: a code that opens a page saying so. */
export const sampleCode = (zone: number): InviteCode => ({ zone, url: `${location.origin}/r/sample`, label: t('inviteScan') });

/**
 * The guests on one card of an event. Guests from before an event had several cards have no
 * card of their own: they are on the event's first.
 */
export function guestsOn(data: EventData | null, projectId: string | undefined): NamedGuest[] {
  if (!data || !projectId) return [];
  return data.guests.filter((g) => (g.projectId ?? data.cards[0]?.projectId) === projectId);
}

/** The answers of some guests, as the server counts them for a whole event. */
export function summaryOf(guests: Guest[]): EventSummary {
  const n = (r: string) => guests.filter((g) => g.response === r).length;
  return {
    guests: guests.length, yes: n('yes'), no: n('no'), maybe: n('maybe'), waiting: guests.filter((g) => !g.response).length,
    coming: guests.filter((g) => g.response === 'yes').reduce((sum, g) => sum + (g.partySize ?? 1), 0),
  };
}

/** An event's name: from this device, or a stand-in when its details are on another one. */
export const eventTitle = (details: LocalEvent | null | undefined) => details?.title ?? t('eventsElsewhereTitle');

/** Archive an event, once the host has said yes: true when it went. */
export async function archiveEvent(event: { id: string; title: string }): Promise<boolean> {
  const yes = await askConfirm({ title: t('eventDeleteTitle', { title: event.title }), body: t('eventDeleteBody'), yes: t('eventDeleteYes'), danger: true });
  if (!yes) return false;
  await api.deleteEvent(event.id);
  return true;
}

/* ------------------------------------------------- changes: the server's half, then this device's */

/** Make an event: the server is told only until when to keep it and its first card; the details stay here. */
export async function createEvent(details: EventDetails, projectId: string | null): Promise<EventFull> {
  const made = await api.createEvent(keepUntilOf(details.startsOn), projectId);
  await putLocalEvent({ id: made.event.id, ...details, startsAt: details.startsAt || null, names: {}, cardIds: projectId ? [projectId] : [] });
  return made;
}

/** Names typed one per line. */
export const namesFrom = (text: string) => text.split(/\r?\n/).map((n) => n.trim().slice(0, 80)).filter(Boolean);

/** Add guests by name to one of the event's cards: the server makes their codes, this device keeps their names. */
export async function addGuests(eventId: string, names: string[], projectId: string): Promise<EventFull> {
  const made = await api.addGuests(eventId, names.length, projectId);
  await updateLocalEvent(eventId, (ev) => ({
    ...ev, names: { ...ev.names, ...Object.fromEntries(made.added.map((id, i) => [id, names[i]])) }, cardIds: [...ev.cardIds, projectId],
  }));
  return made;
}

/** New details, kept here; the server hears only of a new date - the day it deletes the event moves with it. */
export async function saveDetails(eventId: string, details: EventDetails): Promise<void> {
  const before = await getLocalEvent(eventId);
  await updateLocalEvent(eventId, (ev) => ({ ...ev, ...details, startsAt: details.startsAt || null }));
  if (!before || before.startsOn !== details.startsOn) await api.updateEvent(eventId, keepUntilOf(details.startsOn));
}

/** Another card for an event. */
export async function joinEvent(eventId: string, projectId: string): Promise<EventFull> {
  const full = await api.addEventCard(eventId, projectId);
  await updateLocalEvent(eventId, (ev) => ({ ...ev, cardIds: [...ev.cardIds, projectId] }));
  return full;
}

/**
 * An event made before 2026-10-24 still has its details on the server: they come to this
 * device (unless it has them already), and the server's copy is emptied.
 */
export async function takeLegacy(id: string, cardIds: string[]): Promise<void> {
  if (!(await getLocalEvent(id))) {
    const old = await api.legacyEvent(id);
    await putLocalEvent({ id, ...old.details, place: old.details.place ?? '', contact: old.details.contact ?? '', note: old.details.note ?? '',
      names: old.names, cardIds });
  }
  await api.forgetEvent(id);
}

/** Every legacy event in a list moved to this device; true when there was any. */
export async function takeAllLegacy(list: EventBrief[]): Promise<boolean> {
  const old = list.filter((x) => x.event.legacy);
  for (const x of old) await takeLegacy(x.event.id, x.cards.map((c) => c.projectId)).catch(() => {});
  return old.length > 0;
}

/** The events on this device, by id (the events board). */
export async function localEventsById(): Promise<Map<string, LocalEvent>> {
  return new Map((await allLocalEvents().catch(() => [])).map((e) => [e.id, e]));
}

/** The event behind a card, loaded and kept fresh: the server's answers with this device's names. */
export function useEvent(eventId: string | undefined) {
  const [data, setData] = useState<EventData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const load = useCallback(async () => {
    if (!eventId) { setData(null); return; }
    try {
      let full = await api.event(eventId);
      if (full.event.legacy) {
        await takeLegacy(eventId, full.cards.map((c) => c.projectId));
        full = await api.event(eventId);
      }
      setData(withDevice(full, (await getLocalEvent(eventId)) ?? null));
      setError(null);
    } catch (err) {
      setError((err as Error).message);
    }
  }, [eventId]);
  useEffect(() => { void load(); }, [load]);
  // a card file opened, the details edited: the names and details here change
  useEffect(() => onLocalEvents(() => { void load(); }), [load]);
  /** After a change on the server: its answer, with this device's names. */
  const apply = useCallback(async (full: EventFull) => setData(withDevice(full, (await getLocalEvent(full.event.id)) ?? null)), []);
  return { data, error, reload: load, apply };
}

export function blankInput(): EventDetails {
  return {
    title: '', kind: 'birthday', startsOn: '', startsAt: '', timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone || null,
    place: '', contact: '', note: '',
  };
}

/**
 * An event's details, as fields: in the editor's form here, and in the new-card steps when the
 * card is made for an event (pages/new-card.tsx) - inside that page's own form.
 */
export function EventFields({ value: v, onChange, nameRef }: {
  value: EventDetails; onChange: (v: EventDetails) => void; nameRef?: React.Ref<HTMLInputElement>;
}) {
  const set = <K extends keyof EventDetails>(k: K, value: EventDetails[K]) => onChange({ ...v, [k]: value });
  return (
    <>
      <label>{t('eventTitle')}<input ref={nameRef} value={v.title} maxLength={120} required placeholder={t('eventTitleHint')} onChange={(e) => set('title', e.target.value)} /></label>
      <label>{t('eventKind')}
        <select value={v.kind} onChange={(e) => set('kind', e.target.value as EventKind)}>
          {EVENT_KINDS.map((k) => <option key={k} value={k}>{kindLabel(k)}</option>)}
        </select>
      </label>
      <div className="row two">
        <label>{t('eventDate')}<input type="date" value={v.startsOn} required onChange={(e) => set('startsOn', e.target.value)} /></label>
        <label>{t('eventTime')}<input type="time" value={v.startsAt ?? ''} onChange={(e) => set('startsAt', e.target.value)} /></label>
      </div>
      <label>{t('eventPlace')}<textarea rows={2} value={v.place} maxLength={300} placeholder={t('eventPlaceHint')} onChange={(e) => set('place', e.target.value)} /></label>
      <label>{t('eventContact')}<input value={v.contact} maxLength={200} placeholder={t('eventContactHint')} onChange={(e) => set('contact', e.target.value)} /></label>
      <label>{t('eventNote')}<textarea rows={2} value={v.note} maxLength={500} placeholder={t('eventNoteHint')} onChange={(e) => set('note', e.target.value)} /></label>
      <p className="muted small">{t('eventPrivacy')}</p>
    </>
  );
}

function EventForm({ initial, onSave, onCancel, busy }: {
  initial: EventDetails; onSave: (input: EventDetails) => void; onCancel?: () => void; busy: boolean;
}) {
  const [v, setV] = useState(initial);
  const submit = (e: FormEvent) => {
    e.preventDefault();
    onSave({ ...v, startsAt: v.startsAt || null });
  };
  return (
    <form className="stack invite-form" onSubmit={submit}>
      <EventFields value={v} onChange={setV} />
      <div className="row">
        <button className="primary" disabled={busy}>{t('eventSave')}</button>
        {onCancel && <button type="button" onClick={onCancel}>{t('cancel')}</button>}
      </div>
    </form>
  );
}

const ANSWER: Record<string, StringKey> = { yes: 'rsvpYes', no: 'rsvpNo', maybe: 'rsvpMaybe' };

function Chips({ s: x }: { s: EventSummary }) {
  return (
    <>
      <span className="rsvp-chip yes">{t('rsvpYes')} {x.yes}</span>
      <span className="rsvp-chip maybe">{t('rsvpMaybe')} {x.maybe}</span>
      <span className="rsvp-chip no">{t('rsvpNo')} {x.no}</span>
      <span className="rsvp-chip waiting">{t('rsvpWaiting')} {x.waiting}</span>
      <span className="muted small">{t('rsvpComing', { n: x.coming })}</span>
    </>
  );
}

/** A card's name, when it is on this device. */
const titleOf = (projects: ProjectItem[], id: string) => projects.find((p) => p.id === id)?.title ?? t('inviteOtherCard');

/**
 * The editor's Invitation tool: make the card an invitation (a new event, or one you have), the
 * event's details, this card's guests with every answer, the words for the message, and printing
 * a card for each of its guests.
 */
export function InvitePanel({ projectId, invitation, zone, ev, onLinked, onWrite, onPrintAll, onDownloadAll, working }: {
  projectId: string;
  invitation: { eventId: string; zone: number } | undefined;
  /** The card's message frame, or null when its design has none. */
  zone: number | null;
  ev: ReturnType<typeof useEvent>;
  onLinked: (invitation: { eventId: string; zone: number } | undefined) => void;
  /** Put the invitation's words in the message frame. */
  onWrite: (text: string) => void;
  onPrintAll: () => void;
  onDownloadAll: () => void;
  /** A PDF of every guest's card on its way: what to say meanwhile. */
  working: string | null;
}) {
  const [form, setForm] = useState(false);
  const [names, setNames] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** The host's events with their details on this device, to add this card to one of them. */
  const [mine, setMine] = useState<LocalEvent[] | null>(null);
  const [joinId, setJoinId] = useState('');
  /** The cards on this device, for the names of an event's other cards. */
  const [projects, setProjects] = useState<ProjectItem[]>([]);
  const several = (ev.data?.cards.length ?? 0) > 1;
  useEffect(() => { if (several) api.projects().then((r) => setProjects(r.projects), () => {}); }, [several]);
  useEffect(() => {
    if (invitation || zone === null) return;
    void (async () => {
      const live = new Set((await api.events()).events.map((x) => x.event.id));
      const here = (await allLocalEvents()).filter((e) => live.has(e.id)).sort((a, b) => a.startsOn.localeCompare(b.startsOn));
      setMine(here);
      setJoinId(here[0]?.id ?? '');
    })().catch(() => setMine([]));
  }, [invitation, zone]);
  const run = async (job: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    try { await job(); } catch (err) { setError((err as Error).message); } finally { setBusy(false); }
  };

  if (zone === null) return <p className="muted small">{t('inviteNoMessage')}</p>;

  if (!invitation) {
    return (
      <div className="invite">
        <p className="muted small">{t('inviteIntro')}</p>
        {form ? (
          <EventForm initial={blankInput()} busy={busy} onCancel={() => setForm(false)}
            onSave={(input) => void run(async () => {
              const made = await createEvent(input, projectId);
              onLinked({ eventId: made.event.id, zone });
              onWrite(invitationText(input));
              setForm(false);
            })} />
        ) : (
          <>
            <button className="primary" onClick={() => setForm(true)}>{t('inviteMake')}</button>
            {mine && mine.length > 0 && (
              <div className="stack invite-join">
                <label>{t('inviteJoin')}
                  <select value={joinId} onChange={(e) => setJoinId(e.target.value)}>
                    {mine.map((x) => <option key={x.id} value={x.id}>{x.title} · {whenOf(x)}</option>)}
                  </select>
                </label>
                <button disabled={busy || !joinId} onClick={() => void run(async () => {
                  await joinEvent(joinId, projectId);
                  const details = mine.find((x) => x.id === joinId);
                  onLinked({ eventId: joinId, zone });
                  if (details) onWrite(invitationText(details));
                })}>{t('inviteJoinGo')}</button>
                <p className="muted small">{t('inviteJoinHint')}</p>
              </div>
            )}
          </>
        )}
        {error && <p className="error small" role="alert">{error}</p>}
      </div>
    );
  }

  const data = ev.data;
  if (!data) return <p className={ev.error ? 'error small' : 'muted small'}>{ev.error ?? t('loading')}</p>;
  const { event, details } = data;
  // in the archive: nothing to do but bring it back
  if (event.deletedAt) {
    return (
      <div className="invite">
        <div className="invite-event archived">
          <strong>{eventTitle(details)}</strong>
          <span className="small">{t('inviteArchived')}</span>
        </div>
        <button className="primary" disabled={busy} onClick={() => void run(async () => { await ev.apply(await api.restoreEvent(event.id)); })}>
          {t('eventRestore')}
        </button>
        {error && <p className="error small" role="alert">{error}</p>}
      </div>
    );
  }
  const guests = guestsOn(data, projectId);
  const summary = summaryOf(guests);
  return (
    <div className="invite">
      {form && details ? (
        <EventForm initial={{ ...details, startsAt: details.startsAt ?? '' }} busy={busy} onCancel={() => setForm(false)}
          onSave={(input) => void run(async () => {
            await saveDetails(event.id, input);
            setForm(false);
          })} />
      ) : details ? (
        <div className="invite-event">
          <strong>{details.title}</strong>
          <span className="muted small">{kindLabel(details.kind)} · {whenOf(details)}</span>
          {details.place && <span className="muted small">{details.place}</span>}
          <span className="row">
            <button className="small" onClick={() => setForm(true)}>{t('inviteEdit')}</button>
            <button className="small" onClick={() => onWrite(invitationText(details))} title={t('inviteWriteHint')}>{t('inviteWrite')}</button>
          </span>
        </div>
      ) : (
        <div className="invite-event archived">
          <strong>{eventTitle(details)}</strong>
          <span className="small">{t('inviteElsewhere')}</span>
        </div>
      )}

      {several && (
        <p className="muted small">{t('inviteSeveral', { cards: data.cards.length, guests: data.summary.guests, yes: data.summary.yes })}</p>
      )}
      <p className="invite-summary" aria-live="polite">
        {several && <strong className="small">{t('inviteThisCard')}</strong>}
        <Chips s={summary} />
        <button className="link small" onClick={() => void ev.reload()}>{t('reload')}</button>
      </p>

      {details && (
        <form className="stack" onSubmit={(e) => {
          e.preventDefault();
          const list = namesFrom(names);
          if (list.length) void run(async () => { await ev.apply(await addGuests(event.id, list, projectId)); setNames(''); });
        }}>
          <label>{several ? t('inviteGuestsThisCard') : t('inviteGuests')}
            <textarea rows={4} value={names} placeholder={t('inviteGuestsHint')} onChange={(e) => setNames(e.target.value)} />
          </label>
          <button disabled={busy || !names.trim()}>{t('inviteAddGuests')}</button>
        </form>
      )}
      {error && <p className="error small" role="alert">{error}</p>}

      {guests.length > 0 && (
        <ul className="guest-list">
          {guests.map((g) => (
            <li key={g.id}>
              <span className={`guest-name${g.named ? '' : ' muted'}`}>{g.name}</span>
              <span className={`rsvp-chip ${g.response ?? 'waiting'}`}>
                {t(g.response ? ANSWER[g.response] : 'rsvpWaiting')}{g.response !== 'no' && g.partySize && g.partySize > 1 ? ` · ${g.partySize}` : ''}
              </span>
              {several && (
                <select className="guest-move small" value="" aria-label={t('inviteMove', { name: g.name })} title={t('inviteMove', { name: g.name })}
                  onChange={(e) => { const to = e.target.value; if (to) void run(async () => ev.apply(await api.moveGuest(event.id, g.id, to))); }}>
                  <option value="">{t('inviteMoveTo')}</option>
                  {data.cards.filter((c) => c.projectId !== projectId).map((c) => (
                    <option key={c.projectId} value={c.projectId}>{titleOf(projects, c.projectId)}</option>
                  ))}
                </select>
              )}
              <button className="icon small" aria-label={t('inviteRemove', { name: g.name })} title={t('inviteRemove', { name: g.name })}
                onClick={() => void (async () => {
                  if (await askConfirm({ title: t('inviteRemoveConfirm', { name: g.name }), yes: t('inviteRemoveYes'), danger: true })) {
                    await run(async () => {
                      const full = await api.removeGuest(event.id, g.id);
                      await updateLocalEvent(event.id, (x) => { const { [g.id]: _, ...rest } = x.names; return { ...x, names: rest }; });
                      await ev.apply(full);
                    });
                  }
                })()}>×</button>
              {g.message && <em className="small guest-note">“{g.message}”</em>}
            </li>
          ))}
        </ul>
      )}

      <div className="stack">
        <button className="primary" disabled={!guests.length || !!working} onClick={onPrintAll}>{t('invitePrintAll', { n: guests.length })}</button>
        <button disabled={!guests.length || !!working} onClick={onDownloadAll}>{working ?? t('inviteDownloadAll', { n: guests.length })}</button>
        <p className="muted small">{t('invitePrintHint')}</p>
      </div>

      <button className="danger small" disabled={busy} onClick={() => void run(async () => {
        if (await archiveEvent({ id: event.id, title: eventTitle(details) })) await ev.reload();
      })}>{t('inviteDelete')}</button>
    </div>
  );
}
